import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./DealFlow.css";
import ppProfileLogo from "../images/pp_profile.png";
import dealflowLogoTeal from "../images/dealflow.PNG";
import poweredByEdgeLogo from "../images/powered_by_edge.png";
import giftIcon from "../images/gift.png";
import investmentOpportunitiesIcon from "../images/investment-opportunities.png";
import mediaOpportunitiesIcon from "../images/media-opportunities.svg";
import moneyBagIcon from "../images/money bag.png";
import ropeIcon from "../images/rope.png";
import {
  confirmDealflowSubscriptionPayment,
  createDealflowSubscription,
  prepareDealflowSubscriptionPayment,
} from "../utils/dealflowApi";
import { EDGE_CONFIG } from "./edgeConfig";
import { IS_DEALFLOW_DEV_RUNTIME } from "./dealflowRuntimeConfig";

const PLAN_CONFIG = {
  single: { price: 550, name: "Single sport", buttonLabel: "Join" },
  group: { price: 1750, name: "Multi sport", buttonLabel: "Join" },
};

const DEFAULT_BACKGROUND_COLOR = "#EED9B5";

function normalizeHexColor(value) {
  const normalizedValue = value?.trim().replace(/^#/, "");
  return /^[0-9a-fA-F]{6}$/.test(normalizedValue)
    ? `#${normalizedValue.toUpperCase()}`
    : null;
}

function generateUuid() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `pp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function DealFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialBackgroundColor =
    normalizeHexColor(searchParams.get("bg")) || DEFAULT_BACKGROUND_COLOR;
  const [previewBackgroundColor, setPreviewBackgroundColor] = useState(initialBackgroundColor);
  const [backgroundColorInput, setBackgroundColorInput] = useState(initialBackgroundColor);
  const [backgroundCopyStatus, setBackgroundCopyStatus] = useState("");
  const isBackgroundPickerEnabled = searchParams.get("backgroundPicker") === "1";
  const confirmationPath = IS_DEALFLOW_DEV_RUNTIME
    ? "/dev/dealflow/confirmation"
    : "/dealflow/confirmation";
  const [licenseType, setLicenseType] = useState("single");
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [extraRecipientEmail, setExtraRecipientEmail] = useState("");
  const [contactDetails, setContactDetails] = useState({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
  });
  const [billingAddress, setBillingAddress] = useState({
    line_1: "",
    line_2: "",
    city: "",
    zip: "",
    state: "",
    country: "USA",
  });
  const [sdkLoaded, setSdkLoaded] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [purchaseReferenceId, setPurchaseReferenceId] = useState(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [isPreparingSubscriptionPayment, setIsPreparingSubscriptionPayment] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentMethodReady, setIsPaymentMethodReady] = useState(false);
  const [isEdgeFormInteractiveDuringProcessing, setIsEdgeFormInteractiveDuringProcessing] =
    useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const edgeInstanceRef = useRef(null);
  const payButtonRef = useRef(null);
  const lastSubscriptionRequestRef = useRef("");
  const idempotencyKeyRef = useRef("");
  const latestConfirmContextRef = useRef({
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    normalizedBillingAddress: {
      line_1: "",
      line_2: "",
      city: "",
      zip: "",
      state: "",
      country: "",
    },
    hasRequiredBillingAddress: false,
    isPaymentMethodReady: false,
    subscriptionId: "",
    purchaseReferenceId: "",
  });

  const clearIdempotencyKey = () => {
    idempotencyKeyRef.current = "";
  };

  const updateBackgroundPreview = (color) => {
    const normalizedColor = normalizeHexColor(color);
    if (!normalizedColor) return;

    setPreviewBackgroundColor(normalizedColor);
    setBackgroundColorInput(normalizedColor);
    setBackgroundCopyStatus("");

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("bg", normalizedColor.slice(1));
    setSearchParams(nextSearchParams, { replace: true });
  };

  const copyBackgroundHex = async () => {
    try {
      await navigator.clipboard.writeText(previewBackgroundColor);
      setBackgroundCopyStatus("Copied");
    } catch {
      setBackgroundCopyStatus("Copy failed");
    }
  };

  const resetBackgroundPreview = () => {
    setPreviewBackgroundColor(DEFAULT_BACKGROUND_COLOR);
    setBackgroundColorInput(DEFAULT_BACKGROUND_COLOR);
    setBackgroundCopyStatus("");

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("bg");
    setSearchParams(nextSearchParams, { replace: true });
  };

  useEffect(() => {
    if (!document.getElementById("font-awesome-cdn")) {
      const link = document.createElement("link");
      link.id = "font-awesome-cdn";
      link.rel = "stylesheet";
      link.href = "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css";
      document.head.appendChild(link);
    }
  }, []);

  useEffect(() => {
    const loadEdgeSDK = () => {
      if (window.Edge || window.edge || window.EdgeSDK) {
        setSdkLoaded(true);
        return;
      }

      const existingScript = document.getElementById("edge-sdk-script");
      if (existingScript) {
        existingScript.addEventListener("load", () => setSdkLoaded(true));
        existingScript.addEventListener("error", () =>
          setSdkError("Failed to load Edge SDK. Please refresh the page.")
        );
        return;
      }

      const script = document.createElement("script");
      script.id = "edge-sdk-script";
      script.src = EDGE_CONFIG.sdkUrl;
      script.async = true;
      script.onload = () => setSdkLoaded(true);
      script.onerror = () =>
        setSdkError("Failed to load Edge SDK. Please refresh the page.");

      document.body.appendChild(script);
    };

    loadEdgeSDK();
  }, []);

  useEffect(() => {
    if (!isPaymentModalOpen) {
      return;
    }

    const license_key = licenseType === "group" ? "group_license" : "single_license";
    const requestKey = `${license_key}`;
    if (lastSubscriptionRequestRef.current === requestKey && subscriptionId) {
      return;
    }

    let cancelled = false;
    setIsCreatingCheckout(true);
    setSdkError(null);
    setPaymentStatus({ type: "info", message: "Preparing subscription checkout..." });

    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateUuid();
    }
    const idempotency_key = idempotencyKeyRef.current;

    createDealflowSubscription({ license_key, idempotency_key })
      .then((data) => {
        if (cancelled) {
          return;
        }
        console.log("[DealFlow] createSubscription parsed IDs", {
          subscription_id: data.subscription_id,
          purchase_reference_id: data.purchase_reference_id,
        });
        lastSubscriptionRequestRef.current = requestKey;
        setSubscriptionId(data.subscription_id);
        setPurchaseReferenceId(data.purchase_reference_id);
        setPaymentStatus({ type: "info", message: "Subscription checkout is ready." });
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        setSdkError(error.message || "Unable to create checkout.");
      })
      .finally(() => {
        if (!cancelled) {
          setIsCreatingCheckout(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isPaymentModalOpen, licenseType, subscriptionId]);

  const edgeCheckoutId = subscriptionId;
  const normalizedBillingAddressForPrepare = {
    line_1: billingAddress.line_1.trim(),
    line_2: billingAddress.line_2.trim(),
    city: billingAddress.city.trim(),
    zip: billingAddress.zip.trim(),
    state: billingAddress.state.trim(),
    country: billingAddress.country.trim(),
  };
  const hasRequiredBillingAddressForPrepare =
    !!normalizedBillingAddressForPrepare.line_1 &&
    !!normalizedBillingAddressForPrepare.city &&
    !!normalizedBillingAddressForPrepare.zip &&
    !!normalizedBillingAddressForPrepare.state &&
    !!normalizedBillingAddressForPrepare.country;
  latestConfirmContextRef.current = {
    firstName: contactDetails.firstName.trim(),
    lastName: contactDetails.lastName.trim(),
    company: contactDetails.company.trim(),
    email: contactDetails.email.trim(),
    normalizedBillingAddress: normalizedBillingAddressForPrepare,
    hasRequiredBillingAddress: hasRequiredBillingAddressForPrepare,
    isPaymentMethodReady,
    subscriptionId,
    purchaseReferenceId,
  };

  useEffect(() => {
    setIsPaymentMethodReady(false);
    setIsEdgeFormInteractiveDuringProcessing(false);
  }, [edgeCheckoutId]);

  useEffect(() => {
    if (!sdkLoaded || !edgeCheckoutId) {
      return;
    }

    let payButtonClickHandler = null;

    const initializeEdge = async () => {
      try {
        const EdgeClass = window.Edge || window.edge || window.EdgeSDK;
        if (!EdgeClass) {
          throw new Error("Edge SDK not available on window object");
        }

        const edgeClient = new EdgeClass(EDGE_CONFIG.publishableKey, {
          formFactor: "inputs",
        });
        edgeInstanceRef.current = edgeClient;
        console.log("[DealFlow] Edge client initialized", {
          sdkLoaded,
          hasMountPaymentForm: typeof edgeClient.mountPaymentForm === "function",
          hasInitializeForm: typeof edgeClient.initializeForm === "function",
          checkoutId: edgeCheckoutId,
        });

        if (typeof edgeClient.on === "function") {
          edgeClient.on("payment_method_verifying", (payload) => {
            console.log("[DealFlow] Edge event: payment_method_verifying", payload);
            setIsEdgeFormInteractiveDuringProcessing(true);
            setPaymentStatus({ type: "info", message: "Verifying payment method..." });
          });
          edgeClient.on("payment_method_changed", (payload) => {
            console.log("[DealFlow] Edge event: payment_method_changed", payload);
            const ready = payload?.detail?.ready === true || payload?.ready === true;
            setIsPaymentMethodReady(ready);
            if (ready) {
              console.log("[DealFlow] Payment method is ready");
              setPaymentStatus({ type: "info", message: "Payment method is ready." });
            }
          });
          edgeClient.on("payment_method_verified", (payload) => {
            console.log("[DealFlow] Edge event: payment_method_verified", payload);
            setPaymentStatus({
              type: "info",
              message: "Payment method verified. Waiting for approval confirmation...",
            });
          });
          edgeClient.on("payment_approved", (payload) => {
            console.log("[DealFlow] Edge event: payment_approved", payload);
            setIsProcessing(false);
            setIsEdgeFormInteractiveDuringProcessing(false);
            clearIdempotencyKey();
            setPaymentStatus({
              type: "success",
              message:
                "Payment approved. Final status will be confirmed by backend webhook processing.",
            });
            setIsPaymentModalOpen(false);
            navigate(confirmationPath);
          });
          edgeClient.on("subscription_approved", (payload) => {
            console.log("[DealFlow] Edge event: subscription_approved", payload);
            setIsProcessing(false);
            setIsEdgeFormInteractiveDuringProcessing(false);
            clearIdempotencyKey();
            setPaymentStatus({
              type: "success",
              message:
                "Subscription approved. Final status will be confirmed by backend webhook processing.",
            });
            setIsPaymentModalOpen(false);
            navigate(confirmationPath);
          });
          edgeClient.on("payment_failed", (payload) => {
            console.log("[DealFlow] Edge event: payment_failed", payload);
            setIsProcessing(false);
            setIsEdgeFormInteractiveDuringProcessing(false);
            clearIdempotencyKey();
            setPaymentStatus({ type: "error", message: "Payment failed. Please try again." });
          });
          edgeClient.on("payment_timeout", (payload) => {
            console.log("[DealFlow] Edge event: payment_timeout", payload);
            setIsProcessing(false);
            setIsEdgeFormInteractiveDuringProcessing(false);
            clearIdempotencyKey();
            setPaymentStatus({ type: "warning", message: "Payment timed out. Please retry." });
          });
        }

        const paymentButton = payButtonRef.current;
        if (paymentButton) {
          payButtonClickHandler = async function () {
            const confirmContext = latestConfirmContextRef.current;
            const normalizedFirstName = confirmContext.firstName;
            const normalizedLastName = confirmContext.lastName;
            console.log("[DealFlow] Verifying payment method");

            if (!edgeInstanceRef.current || !edgeCheckoutId) {
              console.log("[DealFlow] Payment form is not ready yet");
              setPaymentStatus({ type: "error", message: "Payment form is not ready yet." });
              return;
            }

            if (!confirmContext.email) {
              console.log("[DealFlow] Email is required");
              setPaymentStatus({ type: "error", message: "Email is required." });
              return;
            }
            if (!normalizedFirstName || !normalizedLastName) {
              console.log("[DealFlow] First and last name are required");
              setPaymentStatus({
                type: "error",
                message: "First name and last name are required.",
              });
              return;
            }

            if (!confirmContext.hasRequiredBillingAddress) {
              console.log("[DealFlow] Billing address is required");
              setPaymentStatus({
                type: "error",
                message: "Complete billing address before confirming payment.",
              });
              return;
            }

            if (!confirmContext.isPaymentMethodReady) {
              console.log("[DealFlow] Payment method is not ready");
              setPaymentStatus({
                type: "error",
                message: "Payment method is not ready yet. Complete card details first.",
              });
              return;
            }

            setIsProcessing(true);
            setIsEdgeFormInteractiveDuringProcessing(false);
            setPaymentStatus(null);

            if (!confirmContext.subscriptionId) {
              console.log("[DealFlow] Missing subscription id for subscription checkout");
              setIsProcessing(false);
              setPaymentStatus({
                type: "error",
                message: "Missing subscription id for subscription checkout.",
              });
              return;
            }
            if (!confirmContext.purchaseReferenceId) {
              console.log("[DealFlow] Missing purchase reference id for payment processing");
              setIsProcessing(false);
              setPaymentStatus({
                type: "error",
                message: "Missing purchase reference id for payment processing.",
              });
              return;
            }

            try {
              const license_key = licenseType === "group" ? "group_license" : "single_license";
              const idempotency_key = idempotencyKeyRef.current || generateUuid();
              idempotencyKeyRef.current = idempotency_key;
              console.log("[DealFlow] Calling prepareSubscriptionPayment before verifyPaymentMethod", {
                subscription_id: confirmContext.subscriptionId,
                purchase_reference: confirmContext.purchaseReferenceId,
                license_key,
                idempotency_key,
                email: confirmContext.email,
                billing_address: confirmContext.normalizedBillingAddress,
                company: confirmContext.company || undefined,
              });
              setIsPreparingSubscriptionPayment(true);
              setPaymentStatus({
                type: "info",
                message: "Preparing subscription payment...",
              });

              const prepareResponse = await prepareDealflowSubscriptionPayment({
                subscription_id: confirmContext.subscriptionId,
                purchase_reference: confirmContext.purchaseReferenceId,
                license_key,
                idempotency_key,
                email: confirmContext.email,
                billing_address: confirmContext.normalizedBillingAddress,
                first_name: normalizedFirstName,
                last_name: normalizedLastName,
                company: confirmContext.company || undefined,
              });

              console.log("[DealFlow] prepareSubscriptionPayment response", prepareResponse);
            } catch (error) {
              console.error("[DealFlow] prepareSubscriptionPayment failed", error);
              setIsProcessing(false);
              setPaymentStatus({
                type: "error",
                message: error?.message || "Unable to prepare subscription payment.",
              });
              return;
            } finally {
              setIsPreparingSubscriptionPayment(false);
              setIsEdgeFormInteractiveDuringProcessing(false);
            }

            setPaymentStatus({
              type: "info",
              message: "Verification submitted. Waiting for confirmation events...",
            });

            console.log("[DealFlow] Continuing to verify payment method");

            return await edgeInstanceRef.current
              .verifyPaymentMethod()
              .then(async () => {
                console.log("[DealFlow] Payment method verification resolved");

                if (!subscriptionId) {
                  setIsProcessing(false);
                  setPaymentStatus({
                    type: "error",
                    message: "Missing subscription id for confirmation.",
                  });
                  return;
                }

                try {
                  setPaymentStatus({
                    type: "info",
                    message: "Payment method verified. Confirming subscription payment...",
                  });

                  const confirmResponse = await confirmDealflowSubscriptionPayment({
                    subscription_id: subscriptionId,
                  });
                  console.log("[DealFlow] confirmSubscriptionPayment response", confirmResponse);
                  setPaymentStatus({
                    type: "info",
                    message: "Subscription payment confirmed. Waiting for approval events...",
                  });
                } catch (error) {
                  console.error("[DealFlow] confirmSubscriptionPayment failed", error);
                  setIsProcessing(false);
                  setPaymentStatus({
                    type: "error",
                    message: error?.message || "Unable to confirm subscription payment.",
                  });
                }
              })
              .catch((error) => {
                console.error("[DealFlow] Payment method verification failed", error);
                setIsProcessing(false);
                setIsEdgeFormInteractiveDuringProcessing(false);
                setPaymentStatus({
                  type: "error",
                  message:
                    error?.message ||
                    "Unable to verify payment method. Please check details and retry.",
                });
              });
          };

          paymentButton.addEventListener("click", payButtonClickHandler);
        }

        if (typeof edgeClient.mountPaymentForm !== "function") {
          throw new Error("Edge SDK does not expose mountPaymentForm.");
        }

        const mountNode = document.getElementById("edge-js");
        if (mountNode) {
          mountNode.innerHTML = "";
        }

        console.log("[DealFlow] Calling edgeClient.mountPaymentForm", {
          containerId: "edge-js",
          checkoutId: edgeCheckoutId,
        });
        edgeClient.mountPaymentForm("edge-js", edgeCheckoutId);
        console.log("[DealFlow] mountPaymentForm call returned");
      } catch (error) {
        console.error("[DealFlow] Edge mount/init error", error);
        setSdkError(`Failed to initialize payment form: ${error.message}`);
      }
    };

    initializeEdge();

    return () => {
      if (payButtonClickHandler && payButtonRef.current) {
        payButtonRef.current.removeEventListener("click", payButtonClickHandler);
      }
    };
  }, [sdkLoaded, edgeCheckoutId, navigate]);

  const selectedPlan = PLAN_CONFIG[licenseType];
  const summaryPriceLabel = `$${selectedPlan.price.toLocaleString()}/month`;
  const isModalDisabled = isProcessing;
  const isEdgeFieldInteractive = !isProcessing || isEdgeFormInteractiveDuringProcessing;

  return (
    <div
      className="df-page"
      style={
        isBackgroundPickerEnabled
          ? { "--df-page-background": previewBackgroundColor }
          : undefined
      }
    >
      {isBackgroundPickerEnabled && (
        <aside className="df-background-picker" aria-label="Background color preview controls">
          <div className="df-background-picker-heading">
            <div>
              <div className="df-background-picker-title">Background preview</div>
              <div className="df-background-picker-note">Preview only. The live site is unchanged.</div>
            </div>
            <span
              className="df-background-picker-swatch"
              style={{ backgroundColor: previewBackgroundColor }}
              aria-hidden="true"
            />
          </div>

          <div className="df-background-picker-fields">
            <label className="df-background-picker-color-control">
              <span className="sr-only">Choose background color</span>
              <input
                type="color"
                value={previewBackgroundColor}
                onChange={(event) => updateBackgroundPreview(event.target.value)}
              />
            </label>
            <label className="df-background-picker-hex-control">
              <span>Hex</span>
              <input
                type="text"
                value={backgroundColorInput}
                maxLength={7}
                spellCheck="false"
                onChange={(event) => {
                  const nextValue = event.target.value.toUpperCase();
                  setBackgroundColorInput(nextValue);
                  const normalizedColor = normalizeHexColor(nextValue);
                  if (normalizedColor) updateBackgroundPreview(normalizedColor);
                }}
                onBlur={() => setBackgroundColorInput(previewBackgroundColor)}
                aria-label="Background color hex value"
              />
            </label>
          </div>

          <div className="df-background-picker-actions">
            <button type="button" onClick={copyBackgroundHex}>
              {backgroundCopyStatus || "Copy hex"}
            </button>
            <button type="button" onClick={resetBackgroundPreview}>
              Reset
            </button>
          </div>
        </aside>
      )}
      <main className="df-main">
        <div className="df-main-grid">
          <section className="df-left-col">
            <div className="df-main-brand">
              <img src={dealflowLogoTeal} alt="dealflow" className="df-main-dealflow" />
            </div>
            <h1 className="df-title">
              Private, invite-only <br />
              <span>talent opportunities</span>
            </h1>

            <div className="df-services-strip" aria-label="Services offered">
              <div className="df-service-card df-service-card-plain">
                <div className="df-service-icon-wrap">
                  <img src={moneyBagIcon} alt="" className="df-service-icon" aria-hidden="true" />
                </div>
                <div className="df-service-title">Paid Partnerships</div>
              </div>

              <div className="df-service-card df-service-card-plain">
                <div className="df-service-icon-wrap">
                  <img src={ropeIcon} alt="" className="df-service-icon" aria-hidden="true" />
                </div>
                <div className="df-service-title">PR Appearances</div>
              </div>

              <div className="df-service-card df-service-card-plain">
                <div className="df-service-icon-wrap">
                  <img
                    src={mediaOpportunitiesIcon}
                    alt=""
                    className="df-service-icon df-service-icon-media"
                    aria-hidden="true"
                  />
                </div>
                <div className="df-service-title">Media Opportunities</div>
              </div>

              <div className="df-service-card df-service-card-plain">
                <div className="df-service-icon-wrap">
                  <img
                    src={investmentOpportunitiesIcon}
                    alt=""
                    className="df-service-icon df-service-icon-investment"
                    aria-hidden="true"
                  />
                </div>
                <div className="df-service-title">Investment Opportunities</div>
              </div>

              <div className="df-service-card df-service-card-plain">
                <div className="df-service-icon-wrap">
                  <img src={giftIcon} alt="" className="df-service-icon" aria-hidden="true" />
                </div>
                <div className="df-service-title">Gifting Opportunities</div>
              </div>
            </div>

            <div className="df-tier-header">
            </div>

            <div className="df-plan-section">
              <div className="df-plan-list">
                <div className="df-plan-option">
                  <div className="df-plan-card">
                    <div className="df-plan-main">
                      <div>
                        <div className="df-plan-title-row">
                          <span className="df-plan-title">Single-group</span>
                        </div>
                        <p>
                          (If your company only represents 1 category of talent i.e. just
                          basketball players or musicians)
                        </p>
                      </div>
                    </div>

                    <div className="df-plan-price-block">
                      <div className="df-plan-price-wrap">
                        <div className="df-plan-price">$550</div>
                        <div className="df-plan-price-inline-meta">/month</div>
                      </div>
                      <div className="df-plan-success-fee">with a 10% success fee</div>
                    </div>
                    <button
                      type="button"
                      className="df-plan-cta"
                      onClick={() => {
                        setLicenseType("single");
                        setPaymentStatus(null);
                        setSubscriptionId(null);
                        setPurchaseReferenceId(null);
                        setBillingAddress({
                          line_1: "",
                          line_2: "",
                          city: "",
                          zip: "",
                          state: "",
                          country: "USA",
                        });
                        setIsPreparingSubscriptionPayment(false);
                        lastSubscriptionRequestRef.current = "";
                        clearIdempotencyKey();
                        setIsPaymentModalOpen(true);
                      }}
                    >
                      Select
                    </button>
                  </div>
                </div>

                <div className="df-plan-option">
                  <div className="df-plan-card">
                    <div className="df-plan-main">
                      <div>
                        <div className="df-plan-title-row">
                          <span className="df-plan-title">Multi-group</span>
                        </div>
                        <p>
                          (If your company represents multiple categories of talent i.e.
                          basketball, football, and creators)
                        </p>
                      </div>
                    </div>

                    <div className="df-plan-price-block">
                      <div className="df-plan-price-wrap">
                        <div className="df-plan-price">$1,750</div>
                        <div className="df-plan-price-inline-meta">/month</div>
                      </div>
                      <div className="df-plan-success-fee">with a 10% success fee</div>
                    </div>
                    <button
                      type="button"
                      className="df-plan-cta"
                      onClick={() => {
                        setLicenseType("group");
                        setPaymentStatus(null);
                        setSubscriptionId(null);
                        setPurchaseReferenceId(null);
                        setBillingAddress({
                          line_1: "",
                          line_2: "",
                          city: "",
                          zip: "",
                          state: "",
                          country: "USA",
                        });
                        setIsPreparingSubscriptionPayment(false);
                        lastSubscriptionRequestRef.current = "";
                        clearIdempotencyKey();
                        setIsPaymentModalOpen(true);
                      }}
                    >
                      Select
                    </button>
                  </div>
                </div>
              </div>
            </div>

          </section>
        </div>
      </main>

      {isPaymentModalOpen && (
        <div
          className={`df-modal-backdrop ${isModalDisabled ? "df-modal-disabled" : ""}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby="df-payment-modal-title"
          onClick={(event) => {
            if (!isModalDisabled && event.target === event.currentTarget) {
              clearIdempotencyKey();
              setIsPaymentModalOpen(false);
            }
          }}
        >
          <div className="df-modal-shell">
            <button
              type="button"
              className="df-modal-close"
              aria-label="Close payment"
              disabled={isModalDisabled}
              onClick={() => {
                if (isModalDisabled) {
                  return;
                }
                clearIdempotencyKey();
                setIsPaymentModalOpen(false);
              }}
            >
              <i className="fas fa-times" />
            </button>
            <div className="df-payment-stack">
              <div className="df-payment-card">
                <div className="df-payment-topbar">
                  <h2>
                    <i className="far fa-credit-card" />
                    Payment Details
                  </h2>
                  <div className="df-card-badges">
                    <i className="fab fa-cc-visa df-card-icon df-card-icon-visa" />
                    <i className="fab fa-cc-mastercard df-card-icon df-card-icon-mastercard" />
                    <i className="fab fa-cc-amex df-card-icon df-card-icon-amex" />
                    <i className="fab fa-cc-discover df-card-icon df-card-icon-discover" />
                  </div>
                </div>

                <div className="df-payment-body">
                  <div className="df-summary-row">
                    <div>
                      <p className="df-summary-label">Total due today</p>
                    </div>
                    <div className="df-summary-price">{summaryPriceLabel}</div>
                  </div>

                  <form className="df-form" onSubmit={(event) => event.preventDefault()}>
                    <div className="df-field-group-title">Contact details</div>
                    <div className="df-field-grid">
                      <div className="df-field">
                        <input
                          type="text"
                          placeholder="First name"
                          disabled={isModalDisabled}
                          value={contactDetails.firstName}
                          onChange={(event) =>
                            setContactDetails((prev) => ({ ...prev, firstName: event.target.value }))
                          }
                        />
                      </div>
                      <div className="df-field">
                        <input
                          type="text"
                          placeholder="Last name"
                          disabled={isModalDisabled}
                          value={contactDetails.lastName}
                          onChange={(event) =>
                            setContactDetails((prev) => ({ ...prev, lastName: event.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="df-field">
                      <input
                        type="text"
                        placeholder="Company"
                        disabled={isModalDisabled}
                        value={contactDetails.company}
                        onChange={(event) =>
                          setContactDetails((prev) => ({ ...prev, company: event.target.value }))
                        }
                      />
                    </div>
                    <div className="df-field">
                      <input
                        type="email"
                        placeholder="Email"
                        disabled={isModalDisabled}
                        value={contactDetails.email}
                        onChange={(event) =>
                          setContactDetails((prev) => ({ ...prev, email: event.target.value }))
                        }
                      />
                    </div>
                    <div className="df-field-group-title">Billing address</div>
                    <div className="df-field">
                      <label htmlFor="df-billing-line1" className="df-input-label">
                        Street address
                      </label>
                      <input
                        id="df-billing-line1"
                        type="text"
                        placeholder="Street address"
                        disabled={isModalDisabled}
                        value={billingAddress.line_1}
                        onChange={(event) =>
                          setBillingAddress((prev) => ({ ...prev, line_1: event.target.value }))
                        }
                      />
                    </div>
                    <div className="df-field">
                      <label htmlFor="df-billing-line2" className="df-input-label">
                        Apt / Suite (optional)
                      </label>
                      <input
                        id="df-billing-line2"
                        type="text"
                        placeholder="Apt / Suite (optional)"
                        disabled={isModalDisabled}
                        value={billingAddress.line_2}
                        onChange={(event) =>
                          setBillingAddress((prev) => ({ ...prev, line_2: event.target.value }))
                        }
                      />
                    </div>
                    <div className="df-field-grid">
                      <div className="df-field">
                        <label htmlFor="df-billing-city" className="df-input-label">
                          City
                        </label>
                        <input
                          id="df-billing-city"
                          type="text"
                          placeholder="City"
                          disabled={isModalDisabled}
                          value={billingAddress.city}
                          onChange={(event) =>
                            setBillingAddress((prev) => ({ ...prev, city: event.target.value }))
                          }
                        />
                      </div>
                      <div className="df-field">
                        <label htmlFor="df-billing-state" className="df-input-label">
                          State
                        </label>
                        <input
                          id="df-billing-state"
                          type="text"
                          placeholder="State"
                          disabled={isModalDisabled}
                          value={billingAddress.state}
                          onChange={(event) =>
                            setBillingAddress((prev) => ({ ...prev, state: event.target.value }))
                          }
                        />
                      </div>
                    </div>
                    <div className="df-field-grid">
                      <div className="df-field">
                        <label htmlFor="df-billing-zip" className="df-input-label">
                          ZIP code
                        </label>
                        <input
                          id="df-billing-zip"
                          type="text"
                          placeholder="ZIP code"
                          disabled={isModalDisabled}
                          value={billingAddress.zip}
                          onChange={(event) =>
                            setBillingAddress((prev) => ({ ...prev, zip: event.target.value }))
                          }
                        />
                      </div>
                      <div className="df-field">
                        <label htmlFor="df-billing-country" className="df-input-label">
                          Country
                        </label>
                        <input
                          id="df-billing-country"
                          type="text"
                          placeholder="Country"
                          disabled={isModalDisabled}
                          value={billingAddress.country}
                          onChange={(event) =>
                            setBillingAddress((prev) => ({ ...prev, country: event.target.value }))
                          }
                        />
                      </div>
                    </div>
                    {licenseType === "group" && (
                      <div className="df-multi-email-block">
                        <div className="df-field-group-title df-extra-emails-title">
                          Additional recipient email
                        </div>
                        <div className="df-field df-extra-email-row">
                          <input
                            type="email"
                            placeholder="Additional recipient email"
                            disabled={isModalDisabled}
                            value={extraRecipientEmail}
                            onChange={(event) => setExtraRecipientEmail(event.target.value)}
                          />
                        </div>
                      </div>
                    )}

                    <div className="df-field-group-title">Card information</div>
                    <div className="df-field">
                      <div
                        className={`df-edge-field-wrap${isEdgeFieldInteractive ? " df-edge-field-wrap-active" : ""}`}
                      >
                        {(isCreatingCheckout || !edgeCheckoutId) && !sdkError && (
                          <div className="df-loading">
                            Creating secure checkout...
                          </div>
                        )}
                        {isPreparingSubscriptionPayment && !sdkError && (
                          <div className="df-loading">Preparing payment demand...</div>
                        )}
                        {!sdkLoaded && !sdkError && (
                          <div className="df-loading">Loading secure payment form...</div>
                        )}
                        <div id="edge-js" className="df-edge-container" />
                        {!sdkLoaded && (
                          <input
                            type="text"
                            className="df-card-placeholder-overlay"
                            placeholder="Card number"
                            readOnly
                            tabIndex={-1}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>

                    {sdkError && (
                      <div className="df-alert df-alert-error">
                        <div>
                          <h3>Configuration Error</h3>
                          <p>{sdkError}</p>
                        </div>
                      </div>
                    )}

                    <button
                      ref={payButtonRef}
                      className="df-submit"
                      type="button"
                      disabled={
                        !sdkLoaded ||
                        !!sdkError ||
                        !edgeCheckoutId ||
                        isCreatingCheckout ||
                        isPreparingSubscriptionPayment ||
                        isProcessing ||
                        !isPaymentMethodReady ||
                        !contactDetails.firstName.trim() ||
                        !contactDetails.lastName.trim() ||
                        !contactDetails.email.trim() ||
                        !hasRequiredBillingAddressForPrepare
                      }
                    >
                      <span>{isProcessing ? "Confirming..." : "Confirm"}</span>
                      <i className="fas fa-arrow-right" />
                    </button>
                  </form>
                </div>

                <div className="df-payment-footer">
                  <p className="df-payment-agreement">
                    By joining, you agree to Dealflow&apos;s <a href="/dealflow/terms">Terms</a>{" "}
                    and <a href="/dealflow/privacy">Privacy</a>.
                  </p>
                  <div className="df-payment-footer-meta">
                    <a
                      href="https://tryedge.io/"
                      target="_blank"
                      rel="noreferrer"
                      className="df-powered-footer-link"
                    >
                      <img
                        src={poweredByEdgeLogo}
                        alt="Powered by edge"
                        className="df-powered-footer-image"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="df-footer">
        <div className="df-footer-inner">
          <a href="/" className="df-footer-pp-link" aria-label="Back to deals">
            <img src={ppProfileLogo} alt="Players Punch" className="df-footer-pp" />
          </a>
          <div className="df-footer-left">© 2026 Player&apos;s Punch. All rights reserved.</div>
          <div className="df-footer-links">
            <a className="df-footer-contact" href="/dealflow/terms">
              Terms
            </a>
            <a className="df-footer-contact" href="/dealflow/privacy">
              Privacy
            </a>
            <a className="df-footer-contact" href="mailto:dealflow@playerspunch.com">
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default DealFlow;
